import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";

/** Payment success page — polls /payments/status/{session_id} until it resolves. */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let stopped = false;
    let tries = 0;
    const tick = async () => {
      if (stopped) return;
      tries += 1;
      setAttempts(tries);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setStatus(data);
        if (data.payment_status === "paid" || data.payment_status === "failed" || data.payment_status === "expired") {
          return;
        }
      } catch (e) { /* ignore */ }
      if (tries < 15) setTimeout(tick, 2000);
    };
    tick();
    return () => { stopped = true; };
  }, [sessionId]);

  const paid = status.payment_status === "paid";
  const failed = ["failed", "expired"].includes(status.payment_status);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <Card className="border-border/60 p-8 max-w-md w-full text-center" data-testid="payment-success-page">
        {paid ? (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 text-emerald-600 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h1 className="font-heading text-2xl font-semibold">Pagamento confermato</h1>
            <p className="text-muted-foreground text-sm mt-2">
              Gli allegati di questa opportunità sono ora sbloccati. Puoi scaricarli in qualsiasi momento.
            </p>
            <Button className="mt-6 w-full" onClick={() => navigate(status.property_id ? `/properties/${status.property_id}` : "/properties")} data-testid="go-to-property">
              Vai agli allegati
            </Button>
          </>
        ) : failed ? (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-rose-500/15 text-rose-600 flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8" />
            </div>
            <h1 className="font-heading text-2xl font-semibold">Pagamento non riuscito</h1>
            <p className="text-muted-foreground text-sm mt-2">
              Non è stato possibile completare il pagamento. Riprova o contatta l'assistenza.
            </p>
            <Button variant="outline" className="mt-6 w-full" onClick={() => navigate("/properties")} data-testid="back-to-list">
              Torna all'archivio
            </Button>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary mb-4" />
            <h1 className="font-heading text-xl font-medium">Verifica pagamento in corso…</h1>
            <p className="text-muted-foreground text-xs mt-2">Tentativo {attempts}/15</p>
          </>
        )}
      </Card>
    </div>
  );
}
